import { create } from 'zustand';
import type { FormApi } from 'app/modules/config-driven-form/hooks';

// Shared with LinksCard, which closes the drawer when it covers the page.
export const ASK_MAX_DRAWER_WIDTH = 400;

export interface ChoiceItem {
  id: string;
  label: string;
}

export interface LinkItem {
  label: string;
  subtext?: string;
  destination: 'transaction' | 'form';
  id: string;
}

export type ChatLine =
  | { kind: 'text'; role: 'user' | 'max'; text: string; ts: number }
  | {
      kind: 'choices';
      role: 'max';
      ts: number;
      choiceKind: 'address' | 'agent' | 'option';
      items: ChoiceItem[];
      resolved: boolean;
    }
  | { kind: 'links'; role: 'max'; ts: number; items: LinkItem[] }
  | { kind: 'status'; role: 'max'; text: string; ts: number };

export interface AskMaxSessionInfo {
  conversationId: string;
  sessionId: string;
  formId: string | null;
  transactionId: string | null;
}

export const routeOf = (loc: { pathname: string; search: string }) =>
  `${loc.pathname}${loc.search}`;

export interface AskMaxMirrorHandlers {
  onFieldBlur: (wireKey: string, value: unknown) => void;
  onRowChange: (
    sectionId: string,
    change: { type: 'add' } | { type: 'remove'; index: number }
  ) => void;
}

export type EditorFieldValue = string | number | boolean;

export interface ApplyResult {
  applied: string[];
  unknown: string[];
  invalid: { key: string; reason: string }[];
  filled: { key: string; value: string }[];
}

export interface EditorHandle {
  kind: 'template' | 'document';
  id: string;
  apply: (values: { key: string; value: EditorFieldValue }[]) => ApplyResult;
  focus: (key: string) => boolean;
  finish: () => Promise<
    | { ok: true; documentId?: string }
    | {
        ok: false;
        code: 'invalid_fields' | 'complete_failed' | 'save_failed';
        missing?: string[];
        hint?: string;
      }
  >;
}

interface AskMaxStore {
  open: boolean;
  setOpen: (open: boolean) => void;
  session: AskMaxSessionInfo | null;
  setSession: (session: AskMaxSessionInfo | null) => void;
  setFormId: (formId: string | null) => void;
  lines: ChatLine[];
  appendLine: (line: ChatLine) => void;
  appendStatus: (text: string) => void;
  // Held until Max's next bubble: the tool runs before the reply that introduces the card.
  pendingLinks: Extract<ChatLine, { kind: 'links' }>[];
  queueLinks: (line: Extract<ChatLine, { kind: 'links' }>) => void;
  patchLastLine: (patch: (line: ChatLine) => ChatLine) => void;
  resolveChoices: (ts: number) => void;
  resolveOpenChoices: () => void;
  clearLines: () => void;
  formApi: FormApi | null;
  setFormApi: (api: FormApi | null) => void;
  mirror: AskMaxMirrorHandlers | null;
  setMirror: (h: AskMaxMirrorHandlers | null) => void;
  editor: EditorHandle | null;
  setEditor: (h: EditorHandle | null) => void;
}

export const useAskMaxStore = create<AskMaxStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  session: null,
  setSession: (session) => set({ session }),
  // draft.create and draft.discard own the session's form, not the conversation replies.
  setFormId: (formId) =>
    set((s) => (s.session ? { session: { ...s.session, formId } } : {})),
  lines: [],
  appendLine: (line) =>
    set((s) => {
      if (!s.pendingLinks.length) return { lines: [...s.lines, line] };
      // Anything but a Max bubble means the reply never came, so show the cards now.
      const afterLine = line.kind === 'text' && line.role === 'max';
      return {
        pendingLinks: [],
        lines: afterLine
          ? [...s.lines, line, ...s.pendingLinks]
          : [...s.lines, ...s.pendingLinks, line],
      };
    }),
  appendStatus: (text) =>
    set((s) => ({
      lines: [
        ...s.lines,
        { kind: 'status', role: 'max', text, ts: Date.now() },
      ],
    })),
  pendingLinks: [],
  queueLinks: (line) =>
    set((s) => ({ pendingLinks: [...s.pendingLinks, line] })),
  // Patches the open bubble: a links card flushed behind it is not a target.
  patchLastLine: (patch) =>
    set((s) => {
      let index = s.lines.length - 1;
      while (index >= 0 && s.lines[index].kind === 'links') index -= 1;
      if (index < 0) return {};
      const lines = [...s.lines];
      lines[index] = patch(lines[index]);
      return { lines };
    }),
  resolveChoices: (ts) =>
    set((s) => ({
      lines: s.lines.map((l) =>
        l.kind === 'choices' && l.ts === ts ? { ...l, resolved: true } : l
      ),
    })),
  // Nothing can answer an open card once the conversation ends, so it must not stay clickable.
  resolveOpenChoices: () =>
    set((s) => ({
      lines: s.lines.map((l) =>
        l.kind === 'choices' && !l.resolved ? { ...l, resolved: true } : l
      ),
    })),
  clearLines: () => set({ lines: [], pendingLinks: [] }),
  formApi: null,
  setFormApi: (formApi) => set({ formApi }),
  mirror: null,
  setMirror: (mirror) => set({ mirror }),
  editor: null,
  setEditor: (editor) => set({ editor }),
}));

export const linksLine = (
  items: LinkItem[]
): Extract<ChatLine, { kind: 'links' }> => ({
  kind: 'links',
  role: 'max',
  ts: Date.now(),
  items,
});

// Resolves the moment the handle registers, or null at the timeout.
export const waitForEditor = (
  id: string,
  timeoutMs = 5000
): Promise<EditorHandle | null> =>
  new Promise((resolve) => {
    const { editor } = useAskMaxStore.getState();
    if (editor?.id === id) {
      resolve(editor);
      return;
    }
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    const unsubscribe = useAskMaxStore.subscribe((s) => {
      if (s.editor?.id !== id) return;
      clearTimeout(timer);
      unsubscribe();
      resolve(s.editor);
    });
  });
